import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Alert, CircularProgress, Paper, Button, Chip, Tooltip, IconButton, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { MenuBook, CheckCircle, Warning, Error, Download, Info } from '@mui/icons-material';
import { PredictionMetricsCard } from '../components/Accuracy/PredictionMetricsCard';
import { QuadrantChart, type QuadrantDataPoint } from '../components/Accuracy/QuadrantChart';
import { TimelineChart, TimelineDataPoint } from '../components/Accuracy/TimelineChart';
import { MaturityTrendChartLazy as MaturityTrendChart } from '../components/Accuracy/MaturityTrendChartLazy';
import type { MaturityTrendDataPoint } from '../components/Accuracy/MaturityTrendChart';
import {
  getStockHistorySummary,
  getStockHistoryCumulative,
  getHealthTrends,
  getPredictionMetrics,
  StockHistorySummaryResponse,
  SIGNAL_LABELS,
  type PredictionMetricsData,
  apiRequest
} from '../lib/api';
import { isSmallSample } from '../lib/constants/accuracy';
import { exportTrendDataAsCSV } from '../lib/utils/csv-export';
import { isPredictionMetricsEnabled, getDebugInfo } from '../lib/config/feature-flags';

interface HealthStatus {
  status: 'healthy' | 'degraded' | 'unhealthy' | 'unknown';
  summary: {
    totalMatureSignals: number;
    totalPendingSignals: number;
  };
}

export function Accuracy() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] = useState<StockHistorySummaryResponse | null>(null);
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [trendData, setTrendData] = useState<MaturityTrendDataPoint[]>([]);

  // Prediction metrics state with horizon support
  const [selectedHorizon, setSelectedHorizon] = useState<'1D' | '3D' | '7D'>('1D');
  const [selectedWindow, setSelectedWindow] = useState<string>('30d'); // Filter to avoid legacy data
  const [predictionMetrics, setPredictionMetrics] = useState<Record<string, PredictionMetricsData | null>>({});
  const [allHorizonMetrics, setAllHorizonMetrics] = useState<Record<string, Record<string, PredictionMetricsData | null>>>({});
  const [metricsLoading, setMetricsLoading] = useState(false);

  // Timeline chart state
  const [timelineData, setTimelineData] = useState<TimelineDataPoint[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Parallel fetch
        const [summaryRes, cumulativeRes] = await Promise.all([
          getStockHistorySummary(),
          getStockHistoryCumulative(),
        ]);

        setSummaryData(summaryRes);

        // Fetch health status (non-blocking)
        apiRequest<{ status: string; summary: { totalMatureSignals: number; totalPendingSignals: number } }>('/health/signal-maturity')
          .then(data => {
            setHealthStatus({
              status: data.status as any,
              summary: data.summary
            });
          })
          .catch(err => {
            console.warn('Health check failed:', err);
            // Don't block UI if health check fails
          });

        // Fetch health trends (non-blocking)
        getHealthTrends(30)
          .then(trendsRes => {
            setTrendData(trendsRes.summary);
          })
          .catch(err => {
            console.warn('Health trends fetch failed:', err);
            // Don't block UI if trends fail
          });

        // Phase 1A: Fetch prediction metrics will be triggered by separate useEffect
        // (removed from initial load to allow horizon selection)

        // Map cumulative series to timeline points
        // Add starting point (day before first signal) to show baseline
        const timeline = cumulativeRes.map(p => ({ time: p.date, value: p.equity }));

        // If we have data, prepend starting capital point
        if (timeline.length > 0) {
          const firstDate = new Date(timeline[0].time);
          firstDate.setDate(firstDate.getDate() - 1);
          const startingPoint = {
            time: firstDate.toISOString().split('T')[0],
            value: 1000
          };
          timeline.unshift(startingPoint);
        }

        setTimelineData(timeline);

      } catch (err) {
        console.error('Failed to fetch accuracy data:', err);
        setError('Failed to load dashboard data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch prediction metrics for all horizons whenever window changes
  useEffect(() => {
    const fetchMetrics = async () => {
      const signals = ['STRONG_BUY', 'OPPORTUNITY', 'BULL_TRAP', 'STRONG_SELL'] as const;
      const horizons = ['1D', '3D', '7D'] as const;
      setMetricsLoading(true);

      try {
        // Fetch all signal x horizon combinations in parallel
        const results = await Promise.all(
          signals.flatMap(signal =>
            horizons.map(horizon =>
              getPredictionMetrics({
                signal,
                horizon,
                window: selectedWindow
              })
                .then(res => ({ signal, horizon, data: res.data }))
                .catch(err => {
                  console.warn(`Failed to fetch metrics for ${signal} (${horizon}, ${selectedWindow}):`, err);
                  return { signal, horizon, data: null as PredictionMetricsData | null };
                })
            )
          )
        );

        // Build nested map: signal → horizon → data
        const allMap: Record<string, Record<string, PredictionMetricsData | null>> = {};
        for (const r of results) {
          if (!allMap[r.signal]) allMap[r.signal] = {};
          allMap[r.signal][r.horizon] = r.data;
        }
        setAllHorizonMetrics(allMap);

        // Set current horizon's metrics for backward compat
        const currentMap = Object.fromEntries(
          signals.map(signal => [signal, allMap[signal]?.[selectedHorizon] ?? null])
        );
        setPredictionMetrics(currentMap);
      } catch (err) {
        console.error('Failed to fetch prediction metrics:', err);
      } finally {
        setMetricsLoading(false);
      }
    };

    fetchMetrics();
  }, [selectedWindow]);

  // Update current metrics when horizon selection changes (no re-fetch needed)
  useEffect(() => {
    if (Object.keys(allHorizonMetrics).length === 0) return;
    const signals = ['STRONG_BUY', 'OPPORTUNITY', 'BULL_TRAP', 'STRONG_SELL'] as const;
    const currentMap = Object.fromEntries(
      signals.map(signal => [signal, allHorizonMetrics[signal]?.[selectedHorizon] ?? null])
    );
    setPredictionMetrics(currentMap);
  }, [selectedHorizon, allHorizonMetrics]);

  // Helper: Get signal color
  const getSignalColor = (signal: string): string => {
    switch (signal) {
      case 'STRONG_BUY': return '#2e7d32'; // Green
      case 'OPPORTUNITY': return '#ed6c02'; // Orange
      case 'BULL_TRAP': return '#d32f2f'; // Red
      case 'STRONG_SELL': return '#9c27b0'; // Purple
      default: return '#757575'; // Grey
    }
  };

  // Removed mock timeline generator - timeline now uses backend cumulative series


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            Accuracy Dashboard
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            {healthStatus && (
              <Tooltip
                title={
                  <Box>
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      <strong>System Health: {healthStatus.status}</strong>
                    </Typography>
                    <Typography variant="caption">
                      {healthStatus.summary.totalMatureSignals} mature signals, {healthStatus.summary.totalPendingSignals} pending
                    </Typography>
                  </Box>
                }
              >
                <Chip
                  icon={
                    healthStatus.status === 'healthy' ? <CheckCircle sx={{ fontSize: 16 }} /> :
                    healthStatus.status === 'degraded' ? <Warning sx={{ fontSize: 16 }} /> :
                    <Error sx={{ fontSize: 16 }} />
                  }
                  label={healthStatus.status === 'healthy' ? 'Healthy' : healthStatus.status === 'degraded' ? 'Degraded' : 'Unhealthy'}
                  size="small"
                  color={healthStatus.status === 'healthy' ? 'success' : healthStatus.status === 'degraded' ? 'warning' : 'error'}
                  component="a"
                  href="https://dac-backend.yanggf.workers.dev/health/signal-maturity"
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  sx={{ fontSize: '0.75rem', height: 24, cursor: 'pointer' }}
                />
              </Tooltip>
            )}
            <Button
              variant="outlined"
              size="small"
              startIcon={<MenuBook />}
              href="/docs/ACCURACY_METRICS_GUIDE.md"
              target="_blank"
              rel="noopener noreferrer"
              sx={{ textTransform: 'none' }}
            >
              Metrics Guide
            </Button>
          </Box>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="body1" color="text.secondary">
              Institutional-grade performance tracking for AI signal models with multi-horizon analysis.
            </Typography>
            {summaryData?.last_date && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                Last signal captured: {new Date(summaryData.last_date).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                })}
              </Typography>
            )}
          </Box>
          {summaryData && (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
              Data: <strong>{summaryData.completed_7d}/{summaryData.total_signals}</strong> signals with 7d returns
            </Typography>
          )}
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} data-testid="accuracy-error-alert">
          {error}
        </Alert>
      )}

      {/* Top Row: System Summary */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">System Summary</Typography>
              {summaryData && summaryData.completed_7d < summaryData.total_signals && (
                <Typography
                  variant="caption"
                  color="warning.main"
                  sx={{ fontWeight: 'medium' }}
                  role="status"
                  aria-label={`${summaryData.total_signals - summaryData.completed_7d} signals are currently maturing and pending 7-day return calculations`}
                >
                  ⏳ {summaryData.total_signals - summaryData.completed_7d} signals maturing
                </Typography>
              )}
            </Box>
            <Grid container spacing={2}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Total Signals</Typography>
                <Typography variant="h4">{summaryData?.total_signals || 0}</Typography>
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">7d Accuracy</Typography>
                <Typography variant="h4" color="primary.main">
                  {summaryData?.accuracy_7d ? `${summaryData.accuracy_7d}%` : 'N/A'}
                </Typography>
                {summaryData && isSmallSample(summaryData.completed_7d) && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    (Small sample)
                  </Typography>
                )}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Completed (7d)</Typography>
                <Typography variant="h4">{summaryData?.completed_7d || 0}</Typography>
                {summaryData && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    of {summaryData.total_signals} total
                  </Typography>
                )}
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <Typography variant="caption" color="text.secondary">Unique Stocks</Typography>
                <Typography variant="h4">{summaryData?.unique_symbols || 0}</Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* Signal Prediction Metrics - Phase 1A: Accuracy + Effect Size */}
      {isPredictionMetricsEnabled() && (
        <>
          {/* Horizon and Window Selectors */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
            {/* Horizon Selector */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Horizon:
              </Typography>
              <ToggleButtonGroup
                value={selectedHorizon}
                exclusive
                onChange={(_, newHorizon) => {
                  if (newHorizon !== null) {
                    setSelectedHorizon(newHorizon);
                  }
                }}
                aria-label="prediction horizon"
                size="small"
              >
                <ToggleButton value="1D" aria-label="1 day horizon">
                  1D
                </ToggleButton>
                <ToggleButton value="3D" aria-label="3 day horizon">
                  3D
                </ToggleButton>
                <ToggleButton value="7D" aria-label="7 day horizon">
                  7D
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Prediction Horizon
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>1D:</strong> Fastest data, validates after 1 trading day
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>3D:</strong> Short-term trend validation
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>7D:</strong> Weekly trend (institutional standard)
                  </Typography>
                </Box>
              }>
                <Info sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help' }} />
              </Tooltip>
            </Box>

            {/* Window Filter */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                Window:
              </Typography>
              <ToggleButtonGroup
                value={selectedWindow}
                exclusive
                onChange={(_, newWindow) => {
                  if (newWindow !== null) {
                    setSelectedWindow(newWindow);
                  }
                }}
                aria-label="time window filter"
                size="small"
              >
                <ToggleButton value="30d" aria-label="last 30 days">
                  30d
                </ToggleButton>
                <ToggleButton value="60d" aria-label="last 60 days">
                  60d
                </ToggleButton>
                <ToggleButton value="all" aria-label="all time">
                  All
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title={
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Time Window Filter
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>30d:</strong> Last 30 days only (excludes pre-Nov 24 legacy data)
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>60d:</strong> Last 60 days
                  </Typography>
                  <Typography variant="caption" display="block">
                    • <strong>All:</strong> All historical signals
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: 'italic', color: 'warning.main' }}>
                    Note: Signals before Nov 24 may have incorrect return calculations.
                  </Typography>
                </Box>
              }>
                <Info sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help' }} />
              </Tooltip>
            </Box>
          </Box>

          {/* Metrics Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {(['STRONG_BUY', 'OPPORTUNITY', 'BULL_TRAP', 'STRONG_SELL'] as const).map((signal) => (
              <Grid key={signal} size={{ xs: 12, sm: 6, md: 3 }}>
                <PredictionMetricsCard
                  title={SIGNAL_LABELS[signal]}
                  data={predictionMetrics[signal] || null}
                  loading={metricsLoading}
                  color={getSignalColor(signal)}
                  horizon={selectedHorizon}
                  allHorizons={allHorizonMetrics[signal]}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {!isPredictionMetricsEnabled() && (
        <Alert severity="info" sx={{ mb: 4 }}>
          <Typography variant="h6" component="h3" gutterBottom>
            Prediction Metrics Temporarily Disabled
          </Typography>
          <Typography>
            The new prediction confidence metrics are currently disabled for maintenance.
            Financial simulation metrics are also disabled as we transition to the new system.
          </Typography>
          {getDebugInfo() && (
            <Typography variant="caption" component="pre" sx={{ mt: 1, display: 'block', textAlign: 'left' }}>
              {JSON.stringify(getDebugInfo(), null, 2)}
            </Typography>
          )}
        </Alert>
      )}

      {/* Signal Maturity Trend - Historical tracking */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12 }}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Signal Maturity Trend (Last 30 Days)
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Daily snapshots showing signal maturation progress across all 4 quadrants
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" color="text.secondary">
                  {trendData.length} snapshot{trendData.length !== 1 ? 's' : ''}
                </Typography>
                {trendData.length > 0 && (
                  <Tooltip title="Download 30-day trend data as CSV">
                    <IconButton
                      size="small"
                      onClick={() => exportTrendDataAsCSV(trendData)}
                      aria-label="Download CSV"
                      sx={{ ml: 1 }}
                    >
                      <Download fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            </Box>
            <MaturityTrendChart data={trendData} loading={loading} />
          </Paper>
        </Grid>
      </Grid>

      {/* Middle Row: Charts */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={1} sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Cumulative Model Performance
                </Typography>
                {summaryData && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {summaryData.completed_7d} of {summaryData.total_signals} signals mature
                    ({summaryData.total_signals - summaryData.completed_7d} pending 7-day returns)
                  </Typography>
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {timelineData.length} data point{timelineData.length !== 1 ? 's' : ''}
              </Typography>
            </Box>

            {summaryData && summaryData.completed_7d < summaryData.total_signals && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Limited Data:</strong> Only {summaryData.completed_7d}/{summaryData.total_signals} signals have completed 7-day returns.
                  Chart will update as signals mature (~10 calendar days per signal).
                </Typography>
              </Alert>
            )}

            <TimelineChart
              data={timelineData}
              title=""
              baselineValue={1000}
              baselineLabel="Starting Capital"
              height={400}
            />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <QuadrantChart
            data={Object.entries(predictionMetrics)
              .filter((entry): entry is [string, PredictionMetricsData] => entry[1] !== null)
              .map(([signal, metrics]) => ({
                id: signal,
                label: SIGNAL_LABELS[signal as keyof typeof SIGNAL_LABELS] || signal,
                accuracy: metrics.accuracy.value,
                effectSize: metrics.effect_size?.mean_signed_all != null
                  ? metrics.effect_size.mean_signed_all * 100
                  : 0,
                consistency: metrics.consistency,
                sampleSize: metrics.sample_size,
                color: getSignalColor(signal),
              } satisfies QuadrantDataPoint))}
          />
        </Grid>
      </Grid>
    </>
  );
}
